import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/content";
import { api } from "@/lib/api";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact/submit", form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const mapUrl = `https://www.google.com/maps?q=${COMPANY.mapLat},${COMPANY.mapLng}&hl=en&z=16&output=embed`;

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-offwhite py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-5">
            <span className="gold-hairline" />
            <span className="text-gold text-xs uppercase tracking-[0.28em] font-medium">07 — Contact</span>
          </div>
          <h2 className="font-serif text-navy text-3xl md:text-4xl lg:text-5xl font-light leading-[1.1] tracking-[-0.01em]">
            Start a <em className="text-gold not-italic">conversation.</em>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Contact info & map */}
          <aside className="lg:col-span-5 space-y-8">
            <div className="bg-white border border-softgray p-8 md:p-10">
              <h3 className="font-serif text-navy text-xl mb-6">Salihi Group Head Office</h3>
              <ul className="space-y-5 text-[15px]">
                <li className="flex gap-4">
                  <MapPin size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                  <span className="text-navy leading-relaxed">{COMPANY.address}</span>
                </li>
                <li className="flex gap-4">
                  <Phone size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                  <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`}
                     className="text-navy hover:text-gold" data-testid="contact-phone">{COMPANY.phone}</a>
                </li>
                <li className="flex gap-4">
                  <Mail size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                  <a href={`mailto:${COMPANY.email}`}
                     className="text-navy hover:text-gold" data-testid="contact-email">{COMPANY.email}</a>
                </li>
                <li className="flex gap-4">
                  <Clock size={20} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                  <span className="text-navy">{COMPANY.hours}</span>
                </li>
              </ul>
            </div>

            <div className="aspect-[4/3] w-full border border-softgray overflow-hidden">
              <iframe
                title="Salihi Group location map"
                src={mapUrl}
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: 0, filter: "grayscale(30%) contrast(1.05)" }}
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-softgray p-8 md:p-10" data-testid="contact-form">
              {submitted ? (
                <div className="text-center py-10">
                  <CheckCircle2 size={48} strokeWidth={1.5} className="text-gold mx-auto mb-4" />
                  <h3 className="font-serif text-navy text-2xl">Thank you</h3>
                  <p className="text-mutedink mt-3">Your message has been received. A member of our team will be in touch shortly.</p>
                  <button onClick={() => setSubmitted(false)} className="btn-outline-gold mt-6 text-xs">Send Another</button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name" required value={form.name} onChange={update("name")} testId="contact-input-name" />
                  <Field label="Email" type="email" required value={form.email} onChange={update("email")} testId="contact-input-email" />
                  <Field label="Phone" value={form.phone} onChange={update("phone")} testId="contact-input-phone" />
                  <Field label="Company / Organization" value={form.company} onChange={update("company")} testId="contact-input-company" />
                  <div className="sm:col-span-2">
                    <Field label="Subject" value={form.subject} onChange={update("subject")} testId="contact-input-subject" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">
                      Message <span className="text-gold">*</span>
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      data-testid="contact-input-message"
                      className="w-full border border-softgray px-4 py-3 text-navy font-sans text-[15px] outline-none focus:border-gold transition-colors resize-y"
                    />
                  </div>
                  <div className="sm:col-span-2 pt-2">
                    <button type="submit" className="btn-gold w-full sm:w-auto justify-center" disabled={submitting} data-testid="contact-submit">
                      {submitting ? "Sending…" : (<>Send Message <Send size={14} strokeWidth={2} /></>)}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, required, testId }) {
  return (
    <div>
      <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full border border-softgray px-4 py-3 text-navy font-sans text-[15px] outline-none focus:border-gold transition-colors"
      />
    </div>
  );
}
